import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowLeft, Loader2, Package } from "lucide-react";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { ProductCard } from "../components/ProductCard";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { clientApi } from "../lib/api";
import { useIncrementalList } from "../lib/useIncrementalList";
import { toast } from "sonner";
import { useFavorites } from "../context/FavoritesContext";

export default function MyFavorites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refresh: refreshContext } = useFavorites();
  const { visible, hasMore, sentinelRef } = useIncrementalList(items, { step: 36 });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.get("/portal/saved-favorites");
      setItems(data?.items || []);
    } catch (err) {
      toast.error("No pudimos cargar tus favoritos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await clientApi.get("/portal/saved-favorites");
        if (!cancelled) setItems(data?.items || []);
      } catch (err) {
        if (!cancelled) toast.error("No pudimos cargar tus favoritos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="my-favorites-page">
      <Header />
      <CartSheet />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/catalogo"
          className="inline-flex items-center text-sm text-cm-ink-soft hover:text-cm-red mb-4"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver al catálogo
        </Link>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
              Guardados por ti
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight flex items-center gap-3">
              <Heart className="h-7 w-7 text-cm-red fill-cm-red" />
              Mis favoritos
            </h1>
            <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
              Productos que guardaste para próximas compras. Aún no los has pedido.
              Marca el corazón en cualquier producto del catálogo para agregarlo aquí.
            </p>
          </div>
        </div>

        {loading ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
            <p className="text-sm text-cm-ink-soft mt-3">Cargando tus favoritos...</p>
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Heart className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
            <h2 className="font-serif text-xl mt-4">Aún no has guardado favoritos</h2>
            <p className="text-sm text-cm-ink-soft mt-2 mb-6 max-w-md mx-auto">
              Ve al catálogo y marca con el corazón los productos que quieres guardar para próximas compras.
            </p>
            <Button asChild className="btn-cm-primary" data-testid="empty-favorites-go-catalog">
              <Link to="/catalogo">Ir al catálogo</Link>
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {visible.map((p) => (
              <div key={p.codigo} className="relative" data-testid={`favorite-${p.codigo}`}>
                <ProductCard product={p} onFavoriteToggle={() => { fetchItems(); refreshContext(); }} />
              </div>
            ))}
          </div>
        )}
        {!loading && hasMore && (
          <div ref={sentinelRef} className="py-10 flex justify-center" data-testid="favorites-load-more">
            <Loader2 className="h-5 w-5 animate-spin text-cm-ink-soft" />
          </div>
        )}
      </main>
    </div>
  );
}
